import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, ChevronRight, CheckCircle2, Circle, ExternalLink, Trophy, RotateCcw, Crown } from "lucide-react";

const DONE_KEY = "pbTdTrainingManual";
const readManual = () => { try { return JSON.parse(localStorage.getItem(DONE_KEY) || "{}"); } catch { return {}; } };

export default function TdTrainingScript({ demos }) {
  const [open, setOpen] = useState(false);
  const [selId, setSelId] = useState("");
  const [manual, setManual] = useState(readManual());
  const [cert, setCert] = useState(null);
  useEffect(() => { api.get("/training/certificate/mine").then(({ data }) => data.id && setCert(data)).catch(() => {}); }, []);
  const claimCert = async () => {
    try {
      const { data } = await api.post("/training/certificate", { tournament_id: selId });
      setCert(data);
      toast.success(`Certificate ${data.cert_no} issued — it now shines on your profile 🏅`);
      window.open(`/certificate/${data.id}`, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't issue the certificate"); }
  };
  useEffect(() => {
    if (demos.length && !demos.some((d) => d.id === selId)) setSelId(demos[0].id);
  }, [demos, selId]);
  const t = demos.find((d) => d.id === selId);
  const mkey = (step) => `${selId}:${step}`;
  const toggleManual = (step) => {
    const next = { ...manual, [mkey(step)]: !manual[mkey(step)] };
    setManual(next);
    localStorage.setItem(DONE_KEY, JSON.stringify(next));
  };
  const isElim = t && ["single_elim", "double_elim"].includes(t.format);
  const steps = t ? [
    { id: "create", title: "Create your mock event", done: true,
      tip: "Use the Create Demo Tournament form below — pick the format, divisions, courts, fees and registration window exactly like a real event." },
    { id: "roster", title: "Load the roster", done: (t.entry_count || 0) >= 4,
      tip: "On the demo card, use Add Players (search real members) or Autofill to drop teams into each division. Aim for at least 2 teams per division." },
    { id: "liveflow", title: "Flip Live Player Flow ON", optional: true, done: !!t.live_flow,
      tip: "Optional but recommended for training: participants who are signed in get the real in-app notifications — court calls, score approvals, even the gold letter. No emails or SMS ever." },
    { id: "generate", title: "Generate the draws", done: (t.match_count || 0) > 0,
      tip: "Open the tournament page → Generate Divisions box: tick your divisions, combine short-handed ones, set pools if pool play, assign courts, hit Generate." },
    { id: "scores", title: "Run match play", done: (t.scored_count || 0) > 0,
      tip: "Record scores on the bracket (or approve player submissions when Live Player Flow is on). Courts hand off automatically as matches finish — watch the Full Court Layout." },
    ...(isElim ? [] : [{ id: "finals", title: "Set the finals & medal courts", done: !!t.has_playoffs,
      tip: "When every round-robin/pool match is done, the Set Finals controls appear: pick how many advance, choose playoff courts, designate the 🏆 Championship and ⭐ Center courts, and add a Bronze match." }]),
    { id: "complete", title: "Crown the champions", done: t.status === "complete",
      tip: "Once the gold (and bronze) matches are final, hit Finish Tournament. Champions are recorded — and with Live Player Flow on, players get their champion moment + gold thank-you letter in-app." },
    { id: "debrief", title: "Debrief & reset", done: !!manual[mkey("debrief")], manual: true,
      tip: "Review the flow with your TD, then hit Reset on the demo card — brackets and scores wipe, roster stays, ready to run it again." },
  ] : [];
  const required = steps.filter((s) => !s.optional);
  const doneCount = required.filter((s) => s.done).length;
  const pct = required.length ? Math.round((doneCount / required.length) * 100) : 0;

  return (
    <div className="border border-lime-400/30 bg-lime-400/5" data-testid="td-training-script">
      <button data-testid="td-training-toggle" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-lime-400/5 transition-colors">
        <GraduationCap className="w-5 h-5 text-lime-300 shrink-0" />
        <span className="flex-1">
          <span className="text-sm font-display font-black uppercase text-white">TD Training Script</span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">guided walkthrough of a full mock event</span>
        </span>
        {t && <span className="text-[10px] font-black uppercase tracking-widest text-lime-300" data-testid="td-training-progress">{pct}%</span>}
        <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          {demos.length === 0 ? (
            <div className="text-xs text-zinc-500" data-testid="td-training-empty">
              Step 1 starts below — hit <b className="text-cyan-300">Create Demo Tournament</b> and this script will track your progress automatically.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Training on:</span>
                <select data-testid="td-training-select" value={selId} onChange={(e) => setSelId(e.target.value)}
                  className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white focus:outline-none">
                  {demos.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                {t && (
                  <a href={`/tournaments/${t.id}`} target="_blank" rel="noreferrer" data-testid="td-training-open"
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-cyan-300 hover:text-cyan-200">
                    <ExternalLink className="w-3 h-3" /> Open tournament page
                  </a>
                )}
                <div className="flex-1 min-w-[120px] h-1.5 bg-black/50 border border-lime-400/20">
                  <div className="h-full bg-lime-400 transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <ol className="space-y-1.5">
                {steps.map((s, i) => (
                  <li key={s.id} className={`flex items-start gap-2.5 border px-3 py-2 ${s.done ? "border-lime-400/40 bg-lime-400/5" : "border-pb-border/60"}`}
                    data-testid={`td-step-${s.id}`}>
                    {s.manual ? (
                      <button data-testid={`td-step-check-${s.id}`} onClick={() => toggleManual(s.id)} className="mt-0.5 shrink-0">
                        {s.done ? <CheckCircle2 className="w-4 h-4 text-lime-300" /> : <Circle className="w-4 h-4 text-zinc-600 hover:text-lime-300" />}
                      </button>
                    ) : s.done ? (
                      <CheckCircle2 className="w-4 h-4 text-lime-300 mt-0.5 shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-zinc-600 mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-xs font-bold uppercase tracking-wide">
                        <span className="text-zinc-500 mr-1.5">{i + 1}.</span>
                        <span className={s.done ? "text-lime-200" : "text-white"}>{s.title}</span>
                        {s.optional && <span className="ml-1.5 text-[8px] uppercase tracking-widest text-zinc-600 border border-zinc-700 px-1 py-0.5">optional</span>}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{s.tip}</div>
                    </div>
                  </li>
                ))}
              </ol>
              {pct === 100 && (
                <div className="mt-3 border border-yellow-500/60 bg-yellow-500/10 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="td-training-done">
                  <Trophy className="w-6 h-6 text-yellow-400" />
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-xs font-black uppercase tracking-widest text-yellow-400">Certified ready 🏆</div>
                    <div className="text-[11px] text-zinc-400">You ran a full event start to finish — the real thing works exactly the same way. Hit <RotateCcw className="w-3 h-3 inline" /> Reset on the card to drill it again.</div>
                  </div>
                  {cert ? (
                    <a href={`/certificate/${cert.id}`} target="_blank" rel="noreferrer" data-testid="td-cert-view-btn"
                      className="flex items-center gap-1.5 border-2 border-yellow-500 text-yellow-300 text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:bg-yellow-500 hover:text-black transition-colors">
                      <Crown className="w-4 h-4" /> View {cert.cert_no}
                    </a>
                  ) : (
                    <button data-testid="td-cert-claim-btn" onClick={claimCert}
                      className="flex items-center gap-1.5 bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:bg-yellow-400 transition-colors shadow-[0_0_16px_rgba(212,175,55,0.5)]">
                      <Crown className="w-4 h-4" /> Claim Gold Certificate
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
